import {
  ALLERGY_CATEGORIES,
  ALLERGY_CRITICALITIES,
  ALLERGY_REACTION_SEVERITIES,
  CLINICAL_RULE_APPROVALS,
  OBSERVATION_INTERPRETATIONS,
  VITAL_KEYS,
  VITAL_RULE_KINDS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, codedRefSchema, codedValueSchema, reasonSchema } from './clinical-common.ts';
import { allergySummarySchema } from './patients.ts';

// Triage, vitals, allergy capture and the clinical rules behind them
// (docs/plans/phase1-triage.md §2–4, §3.3). Measured values travel as decimal strings
// ("37.2") and are parsed on the server with `parseVitalValue` from @wodi/domain, so no
// floating point is involved at any step.

export { OBSERVATION_INTERPRETATIONS, VITAL_KEYS, VITAL_RULE_KINDS, CLINICAL_RULE_APPROVALS };

/** The alert rule IDs this slice can raise (plan §4). Stable: override rates are measured by them. */
export const CLINICAL_ALERT_RULES = [
  'vitals.critical_range',
  'triage.allergies_not_recorded',
  'triage.weight_missing_child',
  'triage.immediate_attention',
] as const;
export type ClinicalAlertRule = (typeof CLINICAL_ALERT_RULES)[number];

// --- Recording ------------------------------------------------------------------------------

const decimalText = z
  .string()
  .trim()
  .min(1)
  .max(12)
  .regex(/^\d+(?:[.,]\d*)?$/, 'Numbers only, without the unit');

export const vitalInputSchema = z.object({
  key: z.enum(VITAL_KEYS),
  value: decimalText,
  /** Only for blood glucose: `random` or `fasting`. */
  context: z.string().max(20).nullable().optional(),
});

export type VitalInput = z.infer<typeof vitalInputSchema>;

export const alertAcknowledgementSchema = z.object({
  ruleId: z.enum(CLINICAL_ALERT_RULES),
  reason: reasonSchema,
});

const uniqueVitals = (vitals: readonly VitalInput[]) =>
  new Set(vitals.map((v) => v.key)).size === vitals.length;

export const recordVitalsRequestSchema = z.object({
  /** Generated once per save; a retried request returns the same record. */
  clientRequestId: z.uuid(),
  /** When measured. Absent means now. Not before arrival, not in the future. */
  effectiveAt: z.iso.datetime().optional(),
  vitals: z
    .array(vitalInputSchema)
    .max(20)
    .refine(uniqueVitals, 'Each measurement can be entered once'),
  acknowledgements: z.array(alertAcknowledgementSchema).max(10).default([]),
});

export type RecordVitalsRequest = z.infer<typeof recordVitalsRequestSchema>;

export const recordTriageRequestSchema = recordVitalsRequestSchema.extend({
  chiefComplaint: z.string().trim().min(1).max(500),
  category: codedRefSchema,
});

export type RecordTriageRequest = z.infer<typeof recordTriageRequestSchema>;

export const correctObservationRequestSchema = z.object({
  value: decimalText,
  reason: reasonSchema,
  acknowledgements: z.array(alertAcknowledgementSchema).max(10).default([]),
});

// --- Views ----------------------------------------------------------------------------------

export const ruleSnapshotSchema = z.object({
  ruleId: z.uuid(),
  version: z.number().int(),
  kind: z.enum(VITAL_RULE_KINDS),
  low: z.string().nullable(),
  high: z.string().nullable(),
  approval: z.enum(CLINICAL_RULE_APPROVALS),
  source: z.string(),
});

export type RuleSnapshotView = z.infer<typeof ruleSnapshotSchema>;

export const observationSchema = z.object({
  id: z.uuid(),
  key: z.enum(VITAL_KEYS),
  /** As stored, with the vital's decimals, e.g. "37.2". */
  value: z.string(),
  unit: z.object({ code: z.string(), display: z.string() }),
  context: z.string().nullable(),
  /** Code, display and system version captured at the time, when a coding was configured. */
  coding: codedValueSchema.extend({ version: z.string() }).nullable(),
  interpretation: z.enum(OBSERVATION_INTERPRETATIONS),
  /** The rule the value was judged against when saved; later rule changes don't alter it. */
  rule: ruleSnapshotSchema.nullable(),
  status: z.enum(['final', 'amended', 'entered_in_error']),
  statusReason: z.string().nullable(),
  replacesId: z.uuid().nullable(),
  effectiveAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});

export type ObservationView = z.infer<typeof observationSchema>;

export const vitalSignSetSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  effectiveAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  status: z.enum(['active', 'entered_in_error']),
  statusReason: z.string().nullable(),
  /** Age in days used to choose ranges, and whether it came from an estimated birth date. */
  ageDays: z.number().int().nullable(),
  ageEstimated: z.boolean(),
  /** Every observation in the set, including amended and entered in error ones. */
  observations: z.array(observationSchema),
  /** Derived from the current weight and height, e.g. "24.2"; never stored. */
  bmi: z.string().nullable(),
});

export type VitalSignSetView = z.infer<typeof vitalSignSetSchema>;

export const triageAssessmentSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  chiefComplaint: z.string(),
  category: codedValueSchema.extend({ provisional: z.boolean() }),
  assessedAt: z.iso.datetime(),
  recordedBy: actorSchema,
  status: z.enum(['current', 'superseded', 'entered_in_error']),
  statusReason: z.string().nullable(),
  vitalSignSetId: z.uuid().nullable(),
});

export type TriageAssessmentView = z.infer<typeof triageAssessmentSchema>;

export const allergyRecordSchema = z.object({
  id: z.uuid(),
  substance: z.string(),
  category: z.enum(ALLERGY_CATEGORIES).nullable(),
  criticality: z.enum(ALLERGY_CRITICALITIES).nullable(),
  reaction: z.string().nullable(),
  severity: z.enum(ALLERGY_REACTION_SEVERITIES).nullable(),
  verificationStatus: z.string(),
  clinicalStatus: z.string(),
  recordedAt: z.iso.datetime(),
  statusReason: z.string().nullable(),
});

export type AllergyRecordView = z.infer<typeof allergyRecordSchema>;

/** Everything the triage screen shows for one visit. */
export const triageViewSchema = z.object({
  visitId: z.uuid(),
  patientId: z.uuid(),
  visitStatus: z.enum(['open', 'closed', 'cancelled']),
  visitStartedAt: z.iso.datetime(),
  /** The patient's age in days today, for choosing age-banded rules while typing. */
  patientAgeDays: z.number().int().min(0).nullable(),
  /** The current assessment, and earlier ones (superseded or entered in error). */
  assessment: triageAssessmentSchema.nullable(),
  previousAssessments: z.array(triageAssessmentSchema),
  /** Every vital sign set of the visit, newest first. */
  vitalSets: z.array(vitalSignSetSchema),
  allergies: allergySummarySchema,
  /** Every allergy record of the patient, for entering in error. */
  allergyRecords: z.array(allergyRecordSchema),
  /** The active "No known allergies" record, when there is one. */
  noKnownAllergiesId: z.uuid().nullable(),
});

export type TriageView = z.infer<typeof triageViewSchema>;

// --- Allergies ------------------------------------------------------------------------------

export const recordAllergyRequestSchema = z.object({
  substance: z.string().trim().min(1).max(120),
  category: z.enum(ALLERGY_CATEGORIES).nullable().optional(),
  reaction: z.string().trim().min(1).max(200).nullable().optional(),
  severity: z.enum(ALLERGY_REACTION_SEVERITIES).nullable().optional(),
  criticality: z.enum(ALLERGY_CRITICALITIES).nullable().optional(),
  /** When reported. Absent means now. */
  recordedAt: z.iso.datetime().optional(),
});

export type RecordAllergyRequest = z.infer<typeof recordAllergyRequestSchema>;

// --- Clinical rules (plan §2.3, §2.6–2.7) -----------------------------------------------------

export const vitalRuleSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  vitalKey: z.enum(VITAL_KEYS),
  kind: z.enum(VITAL_RULE_KINDS),
  ageFromDays: z.number().int().min(0),
  ageToDays: z.number().int().min(1).nullable(),
  /** Decimal strings in the vital's unit. */
  low: z.string().nullable(),
  high: z.string().nullable(),
  context: z.string().nullable(),
  approval: z.enum(CLINICAL_RULE_APPROVALS),
  source: z.string(),
  approvedBy: actorSchema.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  updatedBy: actorSchema,
  updatedAt: z.iso.datetime(),
});

export type VitalRuleView = z.infer<typeof vitalRuleSchema>;

export const vitalRuleInputSchema = z.object({
  /** Present to change an existing rule; absent to add one. */
  id: z.uuid().optional(),
  kind: z.enum(VITAL_RULE_KINDS),
  ageFromDays: z.number().int().min(0).max(60_000),
  ageToDays: z.number().int().min(1).max(60_000).nullable(),
  low: decimalText.nullable(),
  high: decimalText.nullable(),
  context: z.string().max(20).nullable(),
  source: z.string().trim().min(3).max(300),
});

/** The whole active set for one vital: rules left out are retired. */
export const replaceVitalRulesRequestSchema = z.object({
  rules: z.array(vitalRuleInputSchema).max(40),
});

export const triageCategorySchema = z.object({
  id: z.uuid(),
  system: z.string(),
  code: z.string(),
  display: z.string(),
  /** Lower is seen first. */
  queueRank: z.number().int().min(1),
  requiresImmediateAttention: z.boolean(),
  provisional: z.boolean(),
  sortOrder: z.number().int(),
});

export type TriageCategoryView = z.infer<typeof triageCategorySchema>;

export const triageCategoryInputSchema = z.object({
  system: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(64),
  display: z.string().trim().min(1).max(80),
  queueRank: z.number().int().min(1).max(99),
  requiresImmediateAttention: z.boolean(),
  sortOrder: z.number().int().min(0).max(10_000),
  status: z.enum(['active', 'inactive']).default('active'),
});

export const vitalCodingSchema = z.object({
  vitalKey: z.enum(VITAL_KEYS),
  codeSystemKey: z.string(),
  code: z.string(),
  /** Display from the active version, or null when the code isn't in it. */
  display: z.string().nullable(),
  status: z.enum(['active', 'inactive']),
});

export const vitalCodingInputSchema = z.object({
  codeSystemKey: z.string().trim().min(1).max(40),
  code: z.string().trim().min(1).max(64),
});

export const triageSettingsSchema = z.object({
  /** Missing weight interrupts below this age (days); null: the rule is off (plan C13). */
  weightRequiredBelowAgeDays: z.number().int().min(1).max(60_000).nullable(),
  /** MUAC shows for ages in [from, to) days; both null: hidden unless added (plan C5). */
  muacFromAgeDays: z.number().int().min(0).max(60_000).nullable(),
  muacToAgeDays: z.number().int().min(1).max(60_000).nullable(),
  /** Flagged patients go ahead of others in the same category (plan P2). */
  queueFlaggedFirst: z.boolean(),
});

export type TriageSettingsView = z.infer<typeof triageSettingsSchema>;

/** Everything the triage form needs to check values as they are typed. */
export const clinicalRulesSchema = z.object({
  rules: z.array(vitalRuleSchema),
  categories: z.array(triageCategorySchema),
  codings: z.array(vitalCodingSchema),
  settings: triageSettingsSchema,
});

export type ClinicalRulesView = z.infer<typeof clinicalRulesSchema>;
